import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, watch, nextTick } from 'vue';
import { uniq } from 'lodash';
import { useRouter } from 'vue-router/composables';
import NavigationLink from '@/ui/chrome/menu/NavigationLink.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'SubNavigation',
  props: {
    subNavigationItems: null,
    measurements: null,
    openState: { type: Boolean }
  },
  emits: ["closeSubmenu", "focusParentLink"],
  setup(__props: any, { emit }: { emit: ({(e: 'closeSubmenu'): void; (e: 'focusParentLink'): void}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    subNavigationItems: INavigationViewObject[],
    measurements: MegamenuMeasurements,
    openState: boolean
}





const submenuItems = ref<INavigationViewObject[]>([]);
const submenuIndex = ref<number>(-1);
const isOpen = ref<boolean>(false);
const activeImage = ref<string | undefined>(null);
const menuImages = ref<string[]>([]);
const menuImageInitialized = ref<{ [key: string]: boolean }>({});
const menuImageLoaded = ref<{ [key: string]: boolean }>({});
const popoutAnimation = ref<boolean>(false);
const subNavigationListWrapper = ref<HTMLDivElement | null>(null);
const bottomListElement = ref<HTMLElement | null>(null);
const numberOfItemsPerSubmenu = 9;
const popout = ref(null);

onMounted(() => {
    submenuItems.value = props.subNavigationItems.slice(0, numberOfItemsPerSubmenu).map(menuItem => sliceSubmenuItems(menuItem));
    menuImages.value = traverseSubmenuImages(submenuItems.value);
    menuImageInitialized.value = convertArrayToObject(menuImages.value);
    menuImageLoaded.value = convertArrayToObject(menuImages.value);
    const popoutElement = popout.value as HTMLElement;
    if (popoutElement) {
        popoutElement.addEventListener('focusout', (event: FocusEvent) => {
            
            const relatedTarget = event.relatedTarget as Node | null;
            if (!popoutElement.contains(relatedTarget)) {
                const tabbableItems = popoutElement.querySelectorAll('[tabindex="0"]');
                tabbableItems.forEach((el: Element) => {
                    (el as HTMLElement).setAttribute('tabindex', '1');
                });
                closeSubmenu();
            }
        });
    }
});

function manageKeydown(event: KeyboardEvent) {
    switch (event.key) {
    case 'Enter':
    case ' ':
        handleEnterOrSpace(event);
        break;
    case 'ArrowRight':
        handleArrowRight(event);
        break;
    case 'ArrowLeft':
        handleArrowLeft(event);
        break;
    case 'ArrowDown':
    case 'ArrowUp':
        handleVerticalNavigation(event);
        break;
    }
}
function handleEnterOrSpace(event: KeyboardEvent) {
    event.preventDefault();
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() === 'a') {
        target.click();
    }
}

function handleArrowRight(event: KeyboardEvent) {
    event.preventDefault();
    const currentSubmenu = subNavigationListWrapper.value?.querySelector('.sub-navigation-submenu.-open');
    const firstLink = currentSubmenu?.querySelector('a');
    firstLink?.focus();
}

function handleArrowLeft(event: KeyboardEvent) {
    event.preventDefault();
    const currentIndex = submenuIndex.value;

    // Reset tabbable elements to avoid auto-focus issues
    subNavigationListWrapper.value?.querySelectorAll('[tabindex="0"]')
        .forEach((el: Element) => (el as HTMLElement).setAttribute('tabindex', '-1'));

    const headerList = popout.value?.querySelectorAll('.c-sub-navigation__list-item-header');
    const submenuLink = headerList?.[currentIndex]?.querySelector('a');
    submenuLink?.focus();
}

function handleVerticalNavigation(event: KeyboardEvent) {
    const currentEl = event.target as HTMLElement;
    const submenuWrapper = subNavigationListWrapper.value;

    const isInSubmenu = submenuWrapper?.contains(currentEl);
    const direction = event.key === 'ArrowDown' ? 1 : -1;

    if (!isInSubmenu) {
        event.preventDefault();
        moveHeaderFocus(direction);
    } else {
        event.preventDefault();
        moveSubmenuFocus(currentEl, direction);
    }
}

function moveHeaderFocus(direction: number) {
    const currentIndex = submenuIndex.value;
    const newIndex = currentIndex + direction;

    if (newIndex >= 0 && newIndex < submenuItems.value.length) {
        setActiveSubmenu(submenuItems.value[newIndex], newIndex);
        nextTick(() => {
            const headerList = popout.value?.querySelectorAll('.c-sub-navigation__list-item-header');
            const submenuLink = headerList?.[newIndex]?.querySelector('a');
            submenuLink?.focus();
        });
    } else if (newIndex < 0) {
        emit('focusParentLink');
    }
}

function moveSubmenuFocus(currentEl: HTMLElement, direction: number) {
    const submenuList = subNavigationListWrapper.value?.querySelectorAll('.sub-navigation-submenu.-open ul li a');
    const items = Array.from(submenuList || []) as HTMLElement[];
    const currentIndex = items.findIndex(el => el === currentEl);
    const nextIndex = currentIndex + direction;

    if (nextIndex >= 0 && nextIndex < items.length) items[nextIndex].focus();
    if (nextIndex < 0) emit('focusParentLink');
    if (nextIndex >= items.length) {
        const bottomLink = bottomListElement.value[0].$el;
        if (bottomLink) bottomLink.focus();
    }   
}

function sliceSubmenuItems(menuItem: INavigationViewObject) : INavigationViewObject {
    const submenu = menuItem.submenu ? menuItem.submenu.slice(0, numberOfItemsPerSubmenu).map(x => sliceSubmenuItems(x)) : menuItem.submenu;
    return {
        path: menuItem.path,
        name: menuItem.name,
        submenu,
        linkId: menuItem.linkId,
        submenuLinkText: menuItem.submenuLinkText,
        submenuImageUrl: menuItem.submenuImageUrl,
        hardRedirect: menuItem.hardRedirect,
        linkType: menuItem.linkType
    };
}

function traverseSubmenuImages(nodes: INavigationViewObject[]) {
    let result: string[] = nodes.map(x => x.submenuImageUrl).filter(x => x);
    for (const child of nodes.filter(x => x.submenu)) {
        result = result.concat(traverseSubmenuImages(child.submenu));
    }
    return uniq(result);
}

function convertArrayToObject(elements: string[]) {
    return elements.reduce((obj, item) => {
        return {
            ...obj,
            [item]: false
        };
    }, {});
}

function imageOpen(image: string) {
    return menuImageLoaded.value[image] && activeImage.value === image;
}

function resetSubmenu() {
    submenuIndex.value = -1;
    activeImage.value = undefined;
}

function setActiveSubmenu(menuItem: INavigationViewObject, k: number) {
    submenuIndex.value = k;
    setActiveImage(menuItem, null);
}

function setActiveImage(menuItem: INavigationViewObject, defaultImageUrl: string) {
    activeImage.value = menuItem.submenuImageUrl || defaultImageUrl;
    menuImageInitialized.value[activeImage.value] = true;
}

watch(() => props.openState, onOpenStateChange);

function onOpenStateChange() {
    isOpen.value = props.openState;
    if (isOpen.value) {
        popoutAnimation.value = true;
        setTimeout(() => popoutAnimation.value = false, 500);
        popout.value.style.height = `max(${props.measurements.minHeight} - ${props.measurements.topMenuCssHeight} - 1px, calc(${props.measurements.contentHeight}px - 1px))`;
        submenuIndex.value = submenuItems.value.length > 0 ? 0 : -1;
        if (submenuItems.value.length > 0) {
            setActiveImage(submenuItems.value[0], null);
        }
    }
    if (!isOpen.value) {
        popoutAnimation.value = false;
        popout.value.style.height = '';
        resetSubmenu();
    }
}

function hasSubMenu(item: INavigationViewObject): boolean {
    return item.submenu && item.submenu.length > 0;
}

function submenuIndexIsActive(index: number): boolean {
    return index === submenuIndex.value;
}

function closeSubmenu(): void {
    emit('closeSubmenu');
}

return { __sfc: true,emit, props, submenuItems, submenuIndex, isOpen, activeImage, menuImages, menuImageInitialized, menuImageLoaded, popoutAnimation, subNavigationListWrapper, bottomListElement, numberOfItemsPerSubmenu, popout, manageKeydown, handleEnterOrSpace, handleArrowRight, handleArrowLeft, handleVerticalNavigation, moveHeaderFocus, moveSubmenuFocus, sliceSubmenuItems, traverseSubmenuImages, convertArrayToObject, imageOpen, resetSubmenu, setActiveSubmenu, setActiveImage, onOpenStateChange, hasSubMenu, submenuIndexIsActive, closeSubmenu }
}

})